import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Table, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { getDayName, formatTime12Hour } from '../../utils/schedulingUtils';
import { unassignSlotPatch } from '../../services/timeSlotService';

const StudentTimeSlots = ({ studentId, onAssignClick }) => {
	const [timeSlots, setTimeSlots] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingSlot, setEditingSlot] = useState(null);
	const [editNotes, setEditNotes] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (studentId) {
			fetchTimeSlots();
		}
	}, [studentId]);

	const fetchTimeSlots = async () => {
		try {
			setLoading(true);
			const timeSlotsRef = collection(db, 'timeSlots');
			const q = query(
				timeSlotsRef,
				where('studentId', '==', studentId),
				where('status', '==', 'assigned')
			);

			const snapshot = await getDocs(q);
			const slots = snapshot.docs.map(doc => ({
				id: doc.id,
				ref: doc.ref,
				...doc.data()
			}));

			// Sort: recurring first, then by day/date
			slots.sort((a, b) => {
				if (a.isRecurring && !b.isRecurring) return -1;
				if (!a.isRecurring && b.isRecurring) return 1;

				if (a.isRecurring) {
					return a.dayOfWeek - b.dayOfWeek;
				} else {
					return new Date(a.specificDate) - new Date(b.specificDate);
				}
			});

			setTimeSlots(slots);
		} catch (error) {
			console.error('Error fetching time slots:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleEditSlot = (slot) => {
		setEditingSlot(slot);
		setEditNotes(slot.notes || '');
		setShowEditModal(true);
	};

	const handleSaveEdit = async () => {
		if (!editingSlot) return;

		setSaving(true);
		try {
			await updateDoc(editingSlot.ref, {
				notes: editNotes.trim(),
				updatedAt: Timestamp.now()
			});

			setShowEditModal(false);
			fetchTimeSlots(); // Refresh list
		} catch (error) {
			console.error('Error updating time slot:', error);
			alert('Error updating time slot. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const handleUnassignSlot = async (slot) => {
		const slotDescription = slot.isRecurring
			? `${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} (${slot.location})`
			: `${new Date(slot.specificDate).toLocaleDateString()} at ${formatTime12Hour(slot.startTime)} (${slot.location})`;

		if (!window.confirm(
			`Unassign this student from this time slot?\n\n${slotDescription}\n\nThe slot will become available for other students.`
		)) {
			return;
		}

		try {
			// Return slot to "open" status instead of deleting. The patch is shared
			// with the Slot Editor's Unassign so both surfaces clear the same
			// per-student residue — see unassignSlotPatch in timeSlotService.
			await updateDoc(slot.ref, unassignSlotPatch());

			fetchTimeSlots(); // Refresh list
		} catch (error) {
			console.error('Error unassigning time slot:', error);
			alert('Error unassigning time slot. Please try again.');
		}
	};

	const handleRemoveSlot = async (slot) => {
		const slotDescription = slot.isRecurring
			? `${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} (${slot.location})`
			: `${new Date(slot.specificDate).toLocaleDateString()} at ${formatTime12Hour(slot.startTime)} (${slot.location})`;

		if (!window.confirm(
			`Are you sure you want to PERMANENTLY DELETE this time slot?\n\n${slotDescription}\n\nThis will remove the slot from your schedule entirely. Use "Unassign" to keep the slot but remove the student.`
		)) {
			return;
		}

		try {
			await deleteDoc(slot.ref);
			fetchTimeSlots(); // Refresh list
		} catch (error) {
			console.error('Error removing time slot:', error);
			alert('Error removing time slot. Please try again.');
		}
	};

	if (loading) {
		return (
			<Card className="shadow-sm">
				<Card.Header style={{ backgroundColor: '#384971', color: '#fff' }}>
					<h5 className="mb-0">
						<i className="fa-sharp fa-solid fa-calendar-clock me-2"></i>
						Scheduled Time Slots
					</h5>
				</Card.Header>
				<Card.Body className="text-center py-4">
					<Spinner animation="border" variant="primary" />
					<p className="mt-2 mb-0">Loading time slots...</p>
				</Card.Body>
			</Card>
		);
	}

	return (
		<Card className="shadow-sm">
			<Card.Header style={{ backgroundColor: '#384971', color: '#fff' }}>
				<div className="d-flex justify-content-between align-items-center">
					<h5 className="mb-0">
						<i className="fa-sharp fa-solid fa-calendar-clock me-2"></i>
						Scheduled Time Slots
					</h5>
					<Button
						variant="light"
						size="sm"
						onClick={onAssignClick}
					>
						<i className="fa-sharp fa-solid fa-plus me-2"></i>
						Assign Time Slot
					</Button>
				</div>
			</Card.Header>
			<Card.Body>
				{timeSlots.length === 0 ? (
					<Alert variant="info" className="mb-0">
						<i className="fa-sharp fa-solid fa-info-circle me-2"></i>
						No time slots assigned yet. Click "Assign Time Slot" to schedule regular or makeup lessons.
					</Alert>
				) : (
					<Table hover responsive>
						<thead>
							<tr>
								<th>Type</th>
								<th>When</th>
								<th>Time</th>
								<th>Duration</th>
								<th>Instructor</th>
								<th>Location</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{timeSlots.map(slot => (
								<tr key={slot.id}>
									<td>
										{slot.isRecurring ? (
											<Badge bg="primary">
												<i className="fa-sharp fa-solid fa-repeat me-1"></i>
												Recurring
											</Badge>
										) : slot.isMakeup ? (
											<Badge bg="warning">
												<i className="fa-sharp fa-solid fa-calendar-day me-1"></i>
												Make-up
											</Badge>
										) : (
											<Badge bg="secondary">
												<i className="fa-sharp fa-solid fa-calendar-day me-1"></i>
												One-time
											</Badge>
										)}
									</td>
									<td>
										{slot.isRecurring ? (
											<span>
												<i className="fa-sharp fa-solid fa-calendar-week me-1"></i>
												<strong>{getDayName(slot.dayOfWeek)}s</strong>
											</span>
										) : (
											<span>
												<i className="fa-sharp fa-solid fa-calendar me-1"></i>
												{new Date(slot.specificDate).toLocaleDateString('en-US', {
													weekday: 'short',
													month: 'short',
													day: 'numeric',
													year: 'numeric'
												})}
											</span>
										)}
									</td>
									<td>
										<strong>{formatTime12Hour(slot.startTime)}</strong>
										{' - '}
										{formatTime12Hour(slot.endTime)}
									</td>
									<td>{slot.duration} min</td>
									<td>
										{slot.instructor
											? <Badge bg="info">{slot.instructor}</Badge>
											: <span className="text-muted">—</span>}
									</td>
									<td>
										<Badge bg="secondary">{slot.location}</Badge>
									</td>
									<td>
										<Button
											variant="outline-primary"
											size="sm"
											className="me-2"
											onClick={() => handleEditSlot(slot)}
											title="Edit notes"
										>
											<i className="fa-sharp fa-solid fa-edit"></i>
										</Button>
										<Button
											variant="outline-warning"
											size="sm"
											className="me-2"
											onClick={() => handleUnassignSlot(slot)}
											title="Unassign student (keeps slot)"
										>
											<i className="fa-sharp fa-solid fa-user-slash"></i>
										</Button>
										<Button
											variant="outline-danger"
											size="sm"
											onClick={() => handleRemoveSlot(slot)}
											title="Delete slot permanently"
										>
											<i className="fa-sharp fa-solid fa-trash"></i>
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				)}

				{timeSlots.some(slot => slot.notes) && (
					<div className="mt-3">
						<h6>Notes:</h6>
						{timeSlots.filter(slot => slot.notes).map(slot => (
							<div key={slot.id} className="mb-2">
								<Badge bg="light" text="dark" className="me-2">
									{slot.isRecurring ? getDayName(slot.dayOfWeek) : new Date(slot.specificDate).toLocaleDateString()}
								</Badge>
								<span className="text-muted">{slot.notes}</span>
							</div>
						))}
					</div>
				)}
			</Card.Body>

			{/* Edit Notes Modal */}
			<Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
				<Modal.Header closeButton style={{ backgroundColor: '#384971', color: '#fff' }}>
					<Modal.Title>
						<i className="fa-sharp fa-solid fa-edit me-2"></i>
						Edit Time Slot Notes
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{editingSlot && (
						<>
							<Alert variant="info" className="mb-3">
								<strong>
									{editingSlot.isRecurring ? getDayName(editingSlot.dayOfWeek) + 's' : new Date(editingSlot.specificDate).toLocaleDateString()}
									{' at '}
									{formatTime12Hour(editingSlot.startTime)}
								</strong>
								<div className="mt-1">
									{editingSlot.instructor && <Badge bg="info" className="me-2">{editingSlot.instructor}</Badge>}
									<Badge bg="secondary">{editingSlot.location}</Badge>
								</div>
							</Alert>

							<Form.Group>
								<Form.Label className="fw-bold">Notes</Form.Label>
								<Form.Control
									as="textarea"
									rows={4}
									value={editNotes}
									onChange={(e) => setEditNotes(e.target.value)}
									placeholder="Add any special notes about this lesson..."
								/>
								<Form.Text className="text-muted">
									These notes will be visible in the schedule and student profile.
								</Form.Text>
							</Form.Group>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
						{saving ? (
							<>
								<Spinner animation="border" size="sm" className="me-2" />
								Saving...
							</>
						) : (
							<>
								<i className="fa-sharp fa-solid fa-save me-2"></i>
								Save Notes
							</>
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</Card>
	);
};

export default StudentTimeSlots;
